import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Sparkles, Check, X, AlertCircle } from 'lucide-react';

/**
 * AI Best Moments. Requests now route through the SAME deduplicated/
 * cached orchestration layer as the Main Editor's AI Tools panel
 * (capability="best_moments") — re-running this for the same video won't
 * re-trigger a new analysis while a cached result is still valid. No real
 * highlight-analysis backend is connected anywhere in this app yet — this
 * stays an honest, contract-complete interface: `available: false` until
 * a real analysis service is wired up server-side. Nothing here is ever a
 * hardcoded/fake result.
 */
export function EditorAiPanel({ suggestions, loading, lastResult, onRequest, onAccept, onReject }) {
  const [numClips, setNumClips] = useState(5);

  return (
    <div className="space-y-3" data-testid="editor-ai-panel">
      <div className="flex items-center gap-2">
        <Input
          type="number" min={1} max={20} value={numClips}
          onChange={(e) => setNumClips(Number(e.target.value))}
          className="w-20 bg-[#1C1F26] border-white/10 text-white" data-testid="ai-num-clips-input"
        />
        <Button
          onClick={() => onRequest(numClips)} disabled={loading}
          className="flex-1 bg-amber-500/90 hover:bg-amber-500 text-black" data-testid="ai-suggest-button"
        >
          <Sparkles className="h-4 w-4 mr-2" />
          {loading ? 'Analyzing…' : 'Suggest Highlights'}
        </Button>
      </div>
      <p className="text-[10px] text-[#555E6E]" data-testid="ai-cache-note">Cached per video — re-running this won't reprocess the same video twice until it changes.</p>

      {lastResult && lastResult.available === false && (
        <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 flex gap-2" data-testid="ai-unavailable-notice">
          <AlertCircle className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
          <p className="text-xs text-amber-200">{lastResult.reason}</p>
        </div>
      )}

      {suggestions.length === 0 && !lastResult && (
        <p className="text-sm text-[#8A93A6] text-center py-4" data-testid="ai-suggestions-empty">
          No suggestions yet. Click "Suggest Highlights" once an analysis backend is connected.
        </p>
      )}

      <div className="space-y-2 max-h-72 overflow-y-auto">
        {suggestions.map((s) => (
          <div key={s.id} className="p-2.5 rounded-lg bg-white/5 border border-white/10 flex items-center justify-between" data-testid={`ai-suggestion-${s.id}`}>
            <div>
              <p className="text-sm text-white">{s.label || 'Suggested moment'}</p>
              <p className="text-xs text-[#8A93A6]">{s.start.toFixed(1)}s – {s.end.toFixed(1)}s</p>
            </div>
            {s.status === 'pending' ? (
              <div className="flex gap-1">
                <button onClick={() => onAccept(s.id)} className="p-1.5 rounded-md bg-green-500/10 text-green-400 hover:bg-green-500/20" data-testid={`ai-accept-${s.id}`}>
                  <Check className="h-4 w-4" />
                </button>
                <button onClick={() => onReject(s.id)} className="p-1.5 rounded-md bg-red-500/10 text-red-400 hover:bg-red-500/20" data-testid={`ai-reject-${s.id}`}>
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <span className={`text-xs px-2 py-0.5 rounded-full ${s.status === 'accepted' ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}>
                {s.status}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
